import { ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { HighlightedText } from "@/components/entitySelector/HighlightedText";

interface EntitySelectorRowProps {
  /** The entity's display name. */
  name: string;
  /** Search query used to highlight the matching part of the name. */
  query?: string;
  /** Secondary line rendered under the name, such as "type - updated 2h ago". */
  meta?: ReactNode;
  /** Right-aligned block, typically a price. */
  price?: ReactNode;
  /** Additional content rendered under the meta line. */
  extraInfo?: ReactNode;
  /** Whether the row is currently selected. */
  isSelected: boolean;
}

/**
 * Renders the body of a selector dropdown row: check icon, name and meta column, then price.
 * The name column absorbs the available width so the price keeps its natural size.
 * @param props - The row content and selection state
 * @returns The row body, intended to be placed inside a CommandItem
 */
export function EntitySelectorRow({
  name,
  query,
  meta,
  price,
  extraInfo,
  isSelected,
}: EntitySelectorRowProps) {
  return (
    <>
      <Check
        className={cn(
          "mr-2 h-4 w-4 shrink-0",
          isSelected ? "opacity-100" : "opacity-0",
        )}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="truncate font-medium">
          <HighlightedText text={name} query={query} />
        </div>
        {meta && (
          <div className="truncate text-xs text-muted-foreground">{meta}</div>
        )}
        {extraInfo}
      </div>
      {price && (
        <div className="ml-2 shrink-0 text-right text-sm tabular-nums">
          {price}
        </div>
      )}
    </>
  );
}
